import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import './Auth.css';
import { useNavigate } from 'react-router-dom';

const ProfileInfo: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        address: '',
        phone: '',
        company: ''
    });
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfile = async () => {
            if (!user) return;
            setLoading(true);
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setForm({
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        address: data.address || '',
                        phone: data.phone || '',
                        company: data.company || ''
                    });
                }
            } catch (e) {
                setError('Failed to load user info');
            }
            setLoading(false);
        };
        fetchProfile();
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: name === 'phone' ? value.replace(/\D/g, '') : value }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        setSaving(true);
        setSuccess('');
        setError('');
        if (!form.firstName || !form.lastName || !form.phone) {
            setError('Please fill in all required fields');
            setSaving(false);
            return;
        }
        if (!/^\d{10}$/.test(form.phone)) {
            setError('Phone number must be exactly 10 digits');
            setSaving(false);
            return;
        }
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                ...form,
                modifyTime: Timestamp.now()
            });
            setSuccess('Saved successfully!');
        } catch (e) {
            setError('Failed to save, please try again');
        }
        setSaving(false);
    };

    if (loading) return <div className="auth-container"><div className="auth-box">Loading...</div></div>;

    return (
        <div className="auth-container">
            <div className="auth-box">
                <h2>Profile Info</h2>
                <form onSubmit={handleSave}>
                    <input
                        type="text"
                        name="firstName"
                        placeholder="First Name*"
                        value={form.firstName}
                        onChange={handleChange}
                        required
                    />
                    <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name*"
                        value={form.lastName}
                        onChange={handleChange}
                        required
                    />
                    <input
                        type="text"
                        name="address"
                        placeholder="Address"
                        value={form.address}
                        onChange={handleChange}
                        autoComplete="street-address"
                    />
                    <input
                        type="tel"
                        name="phone"
                        placeholder="Phone*"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        inputMode="tel"
                        pattern="\d{10}"
                        maxLength={10}
                        onInvalid={e => (e.currentTarget.setCustomValidity('Phone number must be exactly 10 digits'))}
                        onInput={e => (e.currentTarget.setCustomValidity(''))}
                    />
                    <input
                        type="text"
                        name="company"
                        placeholder="Company (optional)"
                        value={form.company}
                        onChange={handleChange}
                    />
                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            width: '100%',
                            background: '#27ae60',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontWeight: 600,
                            fontSize: 18,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            marginBottom: 10
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/change-password')}
                        style={{
                            width: '100%',
                            background: '#f5f5f5',
                            color: '#222',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontWeight: 600,
                            fontSize: 18,
                            cursor: 'pointer',
                            marginBottom: 6
                        }}
                    >
                        Change Password
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        style={{
                            width: '100%',
                            background: '#f5f5f5',
                            color: '#222',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontWeight: 600,
                            fontSize: 18,
                            cursor: 'pointer',
                            marginBottom: 6
                        }}
                    >
                        Back
                    </button>
                </form>
                {success && <div style={{ color: 'green', marginTop: 10 }}>{success}</div>}
                {error && <div style={{ color: 'red', marginTop: 10 }}>{error}</div>}
            </div>
        </div>
    );
};

export default ProfileInfo; 